/**
 * §M1 · Modelo de «Mis perfiles» — DERIVADO, nunca escrito a mano.
 *
 * La pantalla contesta tres preguntas por cada academia en la que el usuario
 * tiene membresía: **qué soy**, **qué me deja hacer** y —la que de verdad
 * importa— **por qué NO puedo hacer el resto**. Esa última distingue dos cosas
 * que el usuario vive igual pero que se arreglan de forma opuesta:
 *
 *  · `role`    → te falta rango. Se resuelve pidiéndoselo a un admin.
 *  · `feature` → tu academia apagó esa capacidad. Ningún ascenso la devuelve;
 *                hay que encender la feature (y los datos siguen ahí).
 *
 * Las tres fuentes son las MISMAS que gobiernan el sistema de verdad:
 *  1. `nav-model` — qué pantallas existen y quién las ve (`visible` + `feature`).
 *  2. Contrato RBAC (`orgRoleGrants` sobre `ORG_CAPABILITIES`) — qué concede el rol.
 *  3. Manifiesto de features — nombre legible, implicaciones y enlace a la guía.
 *
 * Este módulo es PURO (sin hooks ni DOM) para poder probar la matriz completa
 * en vitest: si la explicación derivase del enforcement, el test lo grita.
 */
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
  type FeatureGraphNode,
  ORG_CAPABILITIES,
  type OrgCapability,
  type OrgRole,
  orgRoleGrants,
} from "@space/shared";

import { NAV_SECTIONS, type NavContext, type NavItem } from "@/features/navigation/nav-model";
import { helpTargetForArea } from "@/features/help/registry";
import type { HelpTarget } from "@/features/help/types";

/** Por qué una pantalla no está disponible. Nunca ambas a la vez. */
export type DenialReason = "role" | "feature";

/** Una pantalla del producto, vista desde una membresía concreta. */
export interface ScreenAccess {
  /** `nav-<slug>` — mismo id estable que la barra lateral. */
  testid: string;
  to: string;
  label: MessageDescriptor;
  sectionId: string;
  allowed: boolean;
  /** Solo cuando `allowed === false`. */
  reason?: DenialReason;
  /** Feature que gatea la pantalla, si la hay (venga o no bloqueada). */
  feature?: string;
  /**
   * Key del grafo que realmente bloquea: puede ser un REQUISITO de `feature`,
   * no `feature` misma. Decirle al usuario «falta X» cuando el culpable es su
   * dependencia Y es mandarle a pelear con el interruptor equivocado.
   */
  blockedBy?: string;
  /** Guía + sección que documenta esta pantalla (si el área está mapeada). */
  help?: HelpTarget;
}

/** Una capacidad del plano de organización, vista desde un rol. */
export interface CapabilityAccess {
  capability: OrgCapability;
  granted: boolean;
  label: MessageDescriptor;
  /** Qué significa en la práctica (texto explicativo, §M4). */
  description: MessageDescriptor;
}

/** Etiquetas del contrato RBAC. Exhaustivas por tipo: falta una ⇒ no compila. */
const CAPABILITY_COPY: Record<
  OrgCapability,
  { label: MessageDescriptor; description: MessageDescriptor }
> = {
  ManageOrgSettings: {
    label: msg`Academy settings`,
    description: msg`Change the academy's name, country, time zone, branding and which features are on or off.`,
  },
  ManageCampuses: {
    label: msg`Campuses and rooms`,
    description: msg`Create and edit the academy's physical venues and their rooms. Only useful when the in-person modality is on.`,
  },
  ManageMembers: {
    label: msg`People and roles`,
    description: msg`Invite people, change their role in the academy and see their email address.`,
  },
  ManageAcademics: {
    label: msg`Courses and cohorts`,
    description: msg`Create programs, courses and cohorts, set their schedule and assign teachers.`,
  },
  ManageStudents: {
    label: msg`Students`,
    description: msg`Enroll, transfer and withdraw students, and keep their academic record.`,
  },
  ViewCampusData: {
    label: msg`Read academy data`,
    description: msg`See the cohorts, schedule, attendance and grades of the academy you belong to.`,
  },
};

/** Todas las capacidades resueltas para un rol (`null` ⇒ sin membresía). */
export function capabilitiesForRole(role: OrgRole | null): CapabilityAccess[] {
  return ORG_CAPABILITIES.map((capability) => ({
    capability,
    granted: role !== null && orgRoleGrants(capability, role),
    label: CAPABILITY_COPY[capability].label,
    description: CAPABILITY_COPY[capability].description,
  }));
}

/** Todos los items del nav, aplanados con su sección. */
function allNavItems(): { item: NavItem; sectionId: string }[] {
  return NAV_SECTIONS.flatMap((section) =>
    section.items.map((item) => ({ item, sectionId: section.id })),
  );
}

/** Área de ayuda de una ruta: `/academy/cohorts` → `academy.cohorts` ▸ `academy`. */
export function helpAreaForPath(path: string): HelpTarget | undefined {
  const segments = path.split("/").filter((s) => s.length > 0 && !s.startsWith("$"));
  // De lo MÁS específico a lo más general: una sub-área con guía propia gana.
  for (let depth = Math.min(segments.length, 2); depth >= 1; depth -= 1) {
    const target = helpTargetForArea(segments.slice(0, depth).join("."));
    if (target) {
      return target;
    }
  }
  return undefined;
}

/**
 * Matriz de pantallas para un contexto de navegación.
 *
 * El orden de evaluación es lo que da el «por qué» correcto: primero el ROL
 * (`item.visible`), después la FEATURE. Un alumno no ve «Caja» porque no es
 * cajero — decirle «tu academia apagó la caja» sería mentira, y encenderla no
 * le abriría nada.
 */
export function screensForContext(
  ctx: NavContext,
  blockerOf: (featureKey: string) => string | undefined = () => undefined,
): ScreenAccess[] {
  return allNavItems().map(({ item, sectionId }) => {
    const base = {
      testid: item.testid,
      to: item.to,
      label: item.label,
      sectionId,
      feature: item.feature,
      help: helpAreaForPath(item.to),
    };
    if (!item.visible(ctx)) {
      return { ...base, allowed: false, reason: "role" as const };
    }
    if (item.feature !== undefined && !ctx.featureEnabled(item.feature)) {
      return {
        ...base,
        allowed: false,
        reason: "feature" as const,
        blockedBy: blockerOf(item.feature) ?? item.feature,
      };
    }
    return { ...base, allowed: true };
  });
}

/**
 * §M2 · Guía OFRECIBLE de una pantalla: solo si el usuario puede usarla.
 *
 * Es la regla («nunca mostrar documentación de algo que el usuario no puede
 * usar») expresada UNA vez, para que la pantalla la consuma y el test la fije
 * sobre la misma función — no sobre una condición copiada en el JSX.
 */
export function offeredHelpFor(screen: ScreenAccess): HelpTarget | undefined {
  return screen.allowed ? screen.help : undefined;
}

/** Nombre legible de una feature (manifiesto); cae a la key si no está. */
export function featureLabel(key: string, graph: ReadonlyMap<string, FeatureGraphNode>): string {
  return graph.get(key)?.name ?? key;
}
